import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageBanner from "../../components/PageBanner";
import "../home/homeRedesign.css"; // shared primitives: .btn-ed, .eyebrow-ed, .heading-ed
import "./samashtiNew.css";
import { getSamashti } from '../../services/data.service';
import { mediaUrl, resolveAsset } from '../../services/assetUrl';

const EASE = [0.23, 1, 0.32, 1];
const containerV = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
const itemV = {
  hidden: { opacity: 0, transform: "translateY(22px)" },
  show: { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.45, ease: EASE } },
};

const Rise = ({ children, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, transform: "translateY(26px)" }}
    whileInView={{ opacity: 1, transform: "translateY(0px)" }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, ease: EASE }}
  >
    {children}
  </motion.div>
);

const SectionHead = ({ eyebrow, heading, intro, center = true }) => (
  <div className={`sms-head${center ? " sms-head--center" : ""}`}>
    <span className="eyebrow-ed">{eyebrow}</span>
    <h2 className="heading-ed">{heading}</h2>
    {intro && <p className="sms-head__intro">{intro}</p>}
  </div>
);

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
);
const IconOpen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3h7v7M21 3l-9 9M21 14v5a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h5" /></svg>
);
const sp = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.9, strokeLinecap: "round", strokeLinejoin: "round" };
const STAT_ICONS = {
  volumes: (<svg {...sp}><path d="m12 2 9 5-9 5-9-5 9-5Z" /><path d="m3 12 9 5 9-5" /><path d="m3 17 9 5 9-5" /></svg>),
  issues: (<svg {...sp}><rect x="3" y="4.5" width="18" height="17" rx="2.5" /><path d="M16 2.5v4M8 2.5v4M3 10h18" /></svg>),
  stories: (<svg {...sp}><circle cx="9" cy="8" r="3.1" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M16 6.2a3 3 0 0 1 0 5.6" /><path d="M17 14.4A5.5 5.5 0 0 1 20.5 19" /></svg>),
};
const DISCOVER_ICONS = {
  chronicles: (<svg {...sp}><path d="m12 3 9 5H3l9-5Z" /><path d="M5 11v7M9.5 11v7M14.5 11v7M19 11v7" /><path d="M3 21h18" /></svg>),
  research: (<svg {...sp}><path d="M9 3h6M10 3v6l-5 8.5A2 2 0 0 0 6.8 21h10.4a2 2 0 0 0 1.8-3.5L14 9V3" /><path d="M7.5 15h9" /></svg>),
  spotlight: (<svg {...sp}><path d="m12 3 2.6 5.3 5.9.86-4.27 4.16 1 5.88L12 16.9l-5.23 2.3 1-5.88L2.5 9.16l5.9-.86z" /></svg>),
  faculty: (<svg {...sp}><path d="M3 4h18" /><path d="M4 4v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4" /><path d="M12 15v6M9 21h6" /><path d="m8 11 2.5-2.6 2 2L16 7" /></svg>),
};
const CTA_ICONS = {
  book: (<svg {...sp}><path d="M12 6.4C10.8 5.6 9.2 5.2 7.5 5.2S4.2 5.6 3 6.4v13c1.2-.8 2.8-1.2 4.5-1.2s3.3.4 4.5 1.2m0-13c1.2-.8 2.8-1.2 4.5-1.2s3.3.4 4.5 1.2v13c-1.2-.8-2.8-1.2-4.5-1.2s-3.3.4-4.5 1.2m0-13v13" /></svg>),
  community: (<svg {...sp}><circle cx="9" cy="8" r="3.1" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M16 6.2a3 3 0 0 1 0 5.6" /><path d="M17 14.4A5.5 5.5 0 0 1 20.5 19" /></svg>),
};
const IconSearch = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>);
const IconChevron = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>);

// derive year + quarter ("category") from a date-range string like "Jan – Mar 2025"
const QMAP = { jan: "Q1", feb: "Q1", mar: "Q1", apr: "Q2", may: "Q2", jun: "Q2", jul: "Q3", aug: "Q3", sep: "Q3", oct: "Q4", nov: "Q4", dec: "Q4" };
const QLABEL = { Q1: "Jan – Mar", Q2: "Apr – Jun", Q3: "Jul – Sep", Q4: "Oct – Dec" };
const deriveYear = (v = "") => (v.match(/(\d{4})/) || [])[1] || "";
const deriveQuarter = (v = "") => QMAP[v.trim().slice(0, 3).toLowerCase()] || "";

const SmsDropdown = ({ allLabel, value, options, onSelect }) => {
  const [open, setOpen] = React.useState(false);
  const current = options.find((o) => o.value === value);
  return (
    <div className="sms-dd">
      <button type="button" className={`sms-dd__btn${value ? " is-set" : ""}`} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>{current ? current.label : allLabel}</span>
        <span className="sms-dd__chev">{IconChevron}</span>
      </button>
      {open && (
        <>
          <div className="sms-dd__menu">
            <button type="button" className={`sms-dd__opt${!value ? " is-active" : ""}`} onClick={() => { onSelect(""); setOpen(false); }}>{allLabel}</button>
            {options.map((o) => (
              <button type="button" key={o.value} className={`sms-dd__opt${value === o.value ? " is-active" : ""}`} onClick={() => { onSelect(o.value); setOpen(false); }}>{o.label}</button>
            ))}
          </div>
          <button type="button" className="sms-dd__backdrop" aria-label="Close menu" onClick={() => setOpen(false)} />
        </>
      )}
    </div>
  );
};

const Samashti = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const { hero = {}, about = {}, discover = {}, editions = {}, cta = {} } = data || {};
  const [query, setQuery] = React.useState("");
  const [fVolume, setFVolume] = React.useState("");
  const [fYear, setFYear] = React.useState("");
  const [fQuarter, setFQuarter] = React.useState("");
  const [edShowAll, setEdShowAll] = React.useState(false);
  const EDITIONS_INITIAL = 4;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Override the bundled fallback with backend content (singleton).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getSamashti();
        if (active) setData(res?.data || null);
      } catch (e) {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const years = [...new Set((editions.items || []).map((e) => deriveYear(e.volume)).filter(Boolean))].sort((a, b) => b - a);
  const quarters = [...new Set((editions.items || []).map((e) => deriveQuarter(e.volume)).filter(Boolean))].sort();
  const filteredEditions = (editions.items || []).filter((ed) => {
    const q = query.trim().toLowerCase();
    return (!q || ed.title.toLowerCase().includes(q) || ed.volume.toLowerCase().includes(q))
      && (!fVolume || String(ed.id) === fVolume)
      && (!fYear || deriveYear(ed.volume) === fYear)
      && (!fQuarter || deriveQuarter(ed.volume) === fQuarter);
  });
  useEffect(() => { setEdShowAll(false); }, [query, fVolume, fYear, fQuarter]);
  const visibleEditions = edShowAll ? filteredEditions : filteredEditions.slice(0, EDITIONS_INITIAL);

  const browseEditions = () => {
    const el = document.getElementById("editions");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading || !data) {
    return (
      <div className="sms-page">
        <Header parentMenu="outreach" topbarEnable="enable" />
        <PageBanner title="Samashti" />
        <div className="container" style={{ padding: "6rem 0", textAlign: "center", color: "#53545b" }}>
          {loading ? "Loading…" : "Samashti is currently unavailable. Please check back later."}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="sms-page">
      <Header parentMenu="outreach" topbarEnable="enable" />

      <PageBanner
        title={hero.title}
        eyebrow={hero.eyebrow}
        subtitle={hero.subtitle}
        image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
      />

      {/* ---- About the magazine ---- */}
      <section className="sms-am">
        <div className="container">
          <div className="sms-am__grid">
            <Rise className="sms-am__media">
              <div className="sms-am__cover">
                <img src={mediaUrl(about.image)} alt="Nagarjuna Samashti magazine cover" loading="lazy" />
              </div>
            </Rise>
            <Rise className="sms-am__body">
              <span className="eyebrow-ed">{about.eyebrow}</span>
              <h2 className="sms-am__title">
                {about.headingLines.map((l, i) => <span key={i}>{l}</span>)}
              </h2>
              <p className="sms-am__text">{about.text}</p>
              <div className="sms-am__stats">
                {about.stats.map((s, i) => (
                  <div className="sms-stat" key={i}>
                    <span className="sms-stat__icon">{STAT_ICONS[s.icon]}</span>
                    <span className="sms-stat__value">{s.value}</span>
                    <span className="sms-stat__label">{s.label}</span>
                  </div>
                ))}
              </div>
            </Rise>
          </div>

          {/* What you'll discover */}
          <Rise>
            <div className="sms-disc">
              <h3 className="sms-disc__title">{discover.title}</h3>
              <div className="sms-disc__grid">
                {discover.items.map((it, i) => (
                  <div className="sms-disc__item" key={i}>
                    <span className="sms-disc__icon">{DISCOVER_ICONS[it.icon]}</span>
                    <div className="sms-disc__text">
                      <h4>{it.title}</h4>
                      <p>{it.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Rise>
        </div>
      </section>

      {/* ---- Browse all editions ---- */}
      <section className="sms-br" id="editions">
        <div className="container">
          <div className="sms-br__head">
            <Rise className="sms-br__heading">
              <span className="eyebrow-ed">{editions.eyebrow}</span>
              <h2 className="sms-br__title">{editions.heading}</h2>
            </Rise>
            <div className="sms-br__bar">
              <label className="sms-br__search">
                {IconSearch}
                <input type="text" placeholder="Search editions…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search editions" />
              </label>
              <SmsDropdown allLabel="All Volumes" value={fVolume} onSelect={setFVolume} options={editions.items.map((e) => ({ value: String(e.id), label: `Volume ${e.id}` }))} />
              <SmsDropdown allLabel="All Years" value={fYear} onSelect={setFYear} options={years.map((y) => ({ value: y, label: y }))} />
              <SmsDropdown allLabel="All Categories" value={fQuarter} onSelect={setFQuarter} options={quarters.map((q) => ({ value: q, label: QLABEL[q] }))} />
            </div>
          </div>

          {filteredEditions.length === 0 ? (
            <p className="sms-br__empty">No editions match your filters.</p>
          ) : (
            <>
              <Rise>
                <div className={`sms-br__grid${edShowAll ? " is-scroll" : ""}`}>
                  {visibleEditions.map((ed) => (
                    <article className="sms-ed" key={ed.id}>
                      <a className="sms-ed__cover" href={resolveAsset(editions.pdfBase, ed.pdf)} target="_blank" rel="noopener noreferrer" aria-label={`Read ${ed.title}`}>
                        <img src={resolveAsset(editions.imageBase, ed.img)} alt={ed.title} loading="lazy" />
                        <span className="sms-ed__badge">Vol {ed.id}</span>
                      </a>
                      <div className="sms-ed__body">
                        <span className="sms-ed__date"><IconCalendar /> {ed.volume}</span>
                        <h3 className="sms-ed__title">{ed.title}</h3>
                        <a className="sms-ed__btn" href={resolveAsset(editions.pdfBase, ed.pdf)} target="_blank" rel="noopener noreferrer">Read Edition <IconOpen /></a>
                      </div>
                    </article>
                  ))}
                </div>
              </Rise>
              {filteredEditions.length > EDITIONS_INITIAL && (
                <div className="sms-br__more">
                  <button type="button" className="sms-br__more-btn" onClick={() => setEdShowAll((v) => !v)}>
                    {edShowAll ? "Show Less" : `View More Editions`}
                    <svg className={`sms-br__more-arrow${edShowAll ? " is-up" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ---- Be part of the story ---- */}
      <section className="sms-join">
        <div className="container">
          <Rise>
            <div className="sms-join__box">
              <div className="sms-join__intro">
                <span className="sms-join__eyebrow">{cta.eyebrow}</span>
                <h2 className="sms-join__title">{cta.heading}</h2>
                <p className="sms-join__text">{cta.text}</p>
                <Link to={cta.primary.link} className="sms-join__cta">{cta.primary.text}</Link>
              </div>
              <div className="sms-join__links">
                {cta.links.map((l, i) => (
                  <div className="sms-join__link" key={i}>
                    <span className="sms-join__link-icon">{CTA_ICONS[l.icon]}</span>
                    <div className="sms-join__link-body">
                      <h4>{l.title}</h4>
                      <p>{l.desc}</p>
                      {l.action === "browse"
                        ? <button type="button" className="sms-join__link-btn" onClick={browseEditions}>{l.button}</button>
                        : <Link to={l.link} className="sms-join__link-btn">{l.button}</Link>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Rise>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Samashti;
